import { useEffect, useRef, useState } from 'react';

interface ContextualOverlayProps {
  targetSelector: string;
  padding?: number;
  borderRadius?: number;
}

interface ElementRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * A full-screen dark overlay with a "hole" cut out around the target element.
 * Uses clip-path to create the spotlight effect, independent of z-index stacking contexts.
 */
export function ContextualOverlay({
  targetSelector,
  padding = 10,
  borderRadius = 8,
}: Readonly<ContextualOverlayProps>) {
  const [rect, setRect] = useState<ElementRect | null>(null);
  const isProgrammaticScrollRef = useRef(false);

  // Separate effect for scroll prevention - runs once on mount
  useEffect(() => {
    const preventScroll = (e: WheelEvent | TouchEvent) => {
      // Only prevent manual scrolling, allow programmatic scroll
      if (!isProgrammaticScrollRef.current) {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  useEffect(() => {
    const scrollToElement = () => {
      const element = document.querySelector(targetSelector);
      if (element) {
        // Get the drawer height to account for it when scrolling
        const drawer = document.querySelector('.showme-contextual-drawer');
        const drawerHeight = drawer?.getBoundingClientRect().height ?? 200;

        const elementRect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const visibleAreaHeight = viewportHeight - drawerHeight;

        // Check if element is already visible in the area above the drawer
        const isVisible =
          elementRect.top >= 0 &&
          elementRect.bottom <= visibleAreaHeight;

        if (!isVisible) {
          // Allow programmatic scroll
          isProgrammaticScrollRef.current = true;

          // Calculate scroll position to center element in visible area (above drawer)
          const elementCenter = elementRect.top + elementRect.height / 2;
          const targetScrollY = window.scrollY + elementCenter - visibleAreaHeight / 2;

          window.scrollTo({
            top: Math.max(0, targetScrollY),
            behavior: 'smooth',
          });

          // Re-lock after scroll animation completes
          setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 500);
        }
      }
    };

    // Scroll after a short delay to allow drawer to render and set padding
    const scrollTimeout = setTimeout(scrollToElement, 100);

    const updateRect = () => {
      const element = document.querySelector(targetSelector);
      if (element) {
        const boundingRect = element.getBoundingClientRect();
        setRect({
          top: boundingRect.top - padding,
          left: boundingRect.left - padding,
          width: boundingRect.width + padding * 2,
          height: boundingRect.height + padding * 2,
        });
      } else {
        setRect(null);
      }
    };

    // Update rect after scroll completes
    const rectTimeout = setTimeout(updateRect, 400);

    // Also update immediately for elements already in view
    updateRect();

    // Update on resize/scroll
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    // Use MutationObserver to detect DOM changes
    const observer = new MutationObserver(updateRect);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(rectTimeout);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      observer.disconnect();
    };
  }, [targetSelector, padding]);

  if (!rect) return null;

  // Create clip-path that covers everything EXCEPT the target area
  // Using polygon to create the "hole" effect
  const { top, left, width, height } = rect;
  const right = left + width;
  const bottom = top + height;

  // Clip-path polygon that creates a hole:
  // Start at top-left, go clockwise around the viewport, then create the hole going counter-clockwise
  const clipPath = `polygon(
    0 0,
    100% 0,
    100% 100%,
    0 100%,
    0 0,
    ${left}px ${top}px,
    ${left}px ${bottom}px,
    ${right}px ${bottom}px,
    ${right}px ${top}px,
    ${left}px ${top}px
  )`;

  return (
    <div
      className="showme-contextual-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 100003,
        pointerEvents: 'none',
        clipPath,
      }}
    >
      {/* Border around the cutout to make it more visible */}
      <div
        style={{
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          border: '2px solid rgba(255, 255, 255, 0.3)',
          borderRadius,
          pointerEvents: 'none',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0)',
        }}
      />
    </div>
  );
}
